/**
 * Kotiranje dimenzija preko 3D prikaza (širina × dubina × visina, u cm).
 *
 * Mere su kvadar oko modela (`getDimensions()`); pipeline svodi modele na metre, pa prodavnica
 * ništa ne šalje. Četiri nevidljiva hotspot-a stoje u temenima kvadra, a linije se crtaju u SVG
 * sloju preko viewera, na pozicijama koje vrati `queryHotspot()` posle svake promene kamere.
 * Kote idu na stranu okrenutu kameri (kao na IKEA prikazu), da ne prolaze kroz model.
 */

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface DimensionsViewer extends HTMLElement {
  getDimensions(): Vec3;
  getBoundingBoxCenter(): Vec3;
  getCameraOrbit(): { theta: number; phi: number; radius: number };
  cameraOrbit: string;
  updateHotspot(config: { name: string; position: string }): void;
  queryHotspot(name: string): { canvasPosition: Vec3 } | null;
}

export const DIMENSIONS_STYLE = `
.dim{position:absolute;inset:0;pointer-events:none;z-index:1;opacity:0;transition:opacity .2s}
.dim.on{opacity:1}
.dim svg{position:absolute;inset:0;width:100%;height:100%;overflow:visible}
.dim line{stroke:var(--km3d-dim-color,#0058a3);stroke-width:1.5}
.dim .lbl{position:absolute;transform:translate(-50%,-50%);white-space:nowrap;
font:500 13px/1 system-ui,-apple-system,sans-serif;color:var(--km3d-dim-color,#0058a3);
background:#fff;border:1.5px solid currentColor;border-radius:999px;padding:5px 9px}
.dimbtn{position:absolute;z-index:2;top:12px;left:12px;height:36px;padding:0 14px 0 10px;border:0;
border-radius:999px;background:rgba(255,255,255,.92);color:#0f172a;cursor:pointer;
box-shadow:0 1px 3px rgba(15,23,42,.25);display:inline-flex;align-items:center;gap:6px;
font:600 13px/1 system-ui,-apple-system,sans-serif}
.dimbtn[aria-pressed=true]{background:var(--km3d-dim-color,#0058a3);color:#fff}
.dimbtn:focus-visible{outline:2px solid #2563eb;outline-offset:2px}
.dimbtn svg{width:18px;height:18px;flex:none}
:host(:not([data-state=ready])) .dimbtn{display:none}
`;

/** Lenjir (kotiranje). */
const RULER =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true">' +
  '<path d="M3 17 17 3l4 4L7 21z"/><path d="m7 13 2 2M10 10l2 2M13 7l2 2"/></svg>';

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Koliko se kamera udalji kad se kote uključe: mesto za linije i natpise oko modela. */
const ZOOM_OUT = 1.3;
const POINTS = ['dim-a', 'dim-b', 'dim-c', 'dim-d'] as const;

/** 2.284 m → „228 cm“ (ispod 10 cm sa jednom decimalom). */
export function formatCm(meters: number): string {
  const cm = meters * 100;
  const s = cm < 10 ? (Math.round(cm * 10) / 10).toLocaleString('sr-RS') : String(Math.round(cm));
  return `${s} cm`;
}

/**
 * Temena za kote, u koordinatama modela. Širina ide po prednjoj donjoj ivici (A–B), dubina po
 * bočnoj donjoj (B–C), visina po zadnjoj bočnoj (C–D). „Prednja“ i „bočna“ = strane ka kameri.
 */
export function cornerPoints(center: Vec3, size: Vec3, theta: number): Record<string, Vec3> {
  const hx = size.x / 2;
  const hz = size.z / 2;
  // Mali razmak od modela, da linija ne leži na ivici modela.
  const gap = Math.max(size.x, size.y, size.z) * 0.04;
  const zf = Math.cos(theta) >= 0 ? 1 : -1; // kamera ispred (+Z) ili iza
  const xs = Math.sin(theta) >= 0 ? 1 : -1; // kamera desno (+X) ili levo
  const yMin = center.y - size.y / 2;
  const yMax = center.y + size.y / 2;
  const zFront = center.z + zf * (hz + gap);
  const zBack = center.z - zf * hz;
  const xSide = center.x + xs * (hx + gap);
  return {
    'dim-a': { x: center.x - xs * hx, y: yMin, z: zFront },
    'dim-b': { x: center.x + xs * hx, y: yMin, z: zFront },
    'dim-c': { x: xSide, y: yMin, z: zBack },
    'dim-d': { x: xSide, y: yMax, z: zBack },
  };
}

function clamp(v: number, min: number, max: number): number {
  return min > max ? v : Math.min(Math.max(v, min), max);
}

export class Dimensions {
  readonly button: HTMLButtonElement;
  readonly #layer: HTMLDivElement;
  readonly #svg: SVGSVGElement;
  /** Širina, dubina, visina — svaka kota je linija + natpis. */
  readonly #parts: { line: SVGLineElement; label: HTMLSpanElement }[] = [];
  readonly #onToggle: (shown: boolean) => void;
  #mv: DimensionsViewer | null = null;
  #side = '';
  #frame = 0;
  #shown = false;
  #ro: ResizeObserver | null = null;

  constructor(root: ShadowRoot, onToggle: (shown: boolean) => void) {
    this.#onToggle = onToggle;
    this.#layer = document.createElement('div');
    this.#layer.className = 'dim';
    this.#layer.setAttribute('part', 'dimensions');
    this.#layer.setAttribute('aria-hidden', 'true');
    this.#svg = document.createElementNS(SVG_NS, 'svg');
    // Strelica na oba kraja; `auto-start-reverse` okreće početnu ka spolja.
    this.#svg.innerHTML =
      '<defs><marker id="km3d-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" ' +
      'markerHeight="7" orient="auto-start-reverse"><path d="M0 0 10 5 0 10z" ' +
      'fill="var(--km3d-dim-color,#0058a3)"/></marker></defs>';
    for (let i = 0; i < 3; i++) {
      const l = document.createElementNS(SVG_NS, 'line');
      l.setAttribute('marker-end', 'url(#km3d-arrow)');
      l.setAttribute('marker-start', 'url(#km3d-arrow)');
      const lbl = document.createElement('span');
      lbl.className = 'lbl';
      this.#parts.push({ line: l, label: lbl });
    }
    this.#svg.append(...this.#parts.map((p) => p.line));
    this.#layer.append(this.#svg, ...this.#parts.map((p) => p.label));

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'dimbtn';
    this.button.setAttribute('part', 'dimensions-button');
    this.button.setAttribute('aria-pressed', 'false');
    // Vidljiv tekst je naziv dugmeta; uključeno/isključeno nosi aria-pressed.
    this.button.innerHTML = `${RULER}<span>Dimenzije</span>`;
    this.button.addEventListener('click', () => this.toggle(!this.#shown));
    root.append(this.#layer, this.button);
  }

  get shown(): boolean {
    return this.#shown;
  }

  /** Model je učitan: postavi hotspot-e i natpise. */
  attach(mv: DimensionsViewer): void {
    if (this.#mv === mv) return;
    this.detach();
    this.#mv = mv;
    const size = mv.getDimensions();
    // Redosled kota: širina (x), dubina (z), visina (y).
    const values = [size.x, size.z, size.y];
    this.#parts.forEach((p, i) => (p.label.textContent = formatCm(values[i] ?? 0)));
    this.button.setAttribute(
      'title',
      `Š ${formatCm(size.x)} × D ${formatCm(size.z)} × V ${formatCm(size.y)}`,
    );
    for (const name of POINTS) {
      const h = document.createElement('div');
      h.slot = `hotspot-${name}`;
      h.dataset.position = '0m 0m 0m';
      h.dataset.normal = '0m 1m 0m';
      h.style.cssText = 'width:0;height:0;pointer-events:none';
      mv.append(h);
    }
    this.#side = '';
    mv.addEventListener('camera-change', this.#schedule);
    this.#ro = new ResizeObserver(this.#schedule);
    this.#ro.observe(mv);
    // `dimensions="show"`: kote su uključene pre nego što je model stigao.
    if (this.#shown) this.#zoom(ZOOM_OUT);
    this.#schedule();
  }

  detach(): void {
    if (!this.#mv) return;
    this.#mv.removeEventListener('camera-change', this.#schedule);
    this.#mv.querySelectorAll('[slot^="hotspot-dim-"]').forEach((n) => n.remove());
    this.#ro?.disconnect();
    this.#ro = null;
    this.#mv = null;
    cancelAnimationFrame(this.#frame);
  }

  toggle(show: boolean): void {
    if (show === this.#shown) return;
    this.#shown = show;
    this.#layer.classList.toggle('on', show);
    this.#zoom(show ? ZOOM_OUT : 1 / ZOOM_OUT);
    this.button.setAttribute('aria-pressed', String(show));
    if (show) this.#schedule();
    this.#onToggle(show);
  }

  /** Glatko udaljavanje/približavanje (model-viewer interpolira), ugao ostaje korisnikov. */
  #zoom(factor: number): void {
    const mv = this.#mv;
    if (!mv) return;
    const { theta, phi, radius } = mv.getCameraOrbit();
    mv.cameraOrbit = `${theta}rad ${phi}rad ${radius * factor}m`;
  }

  #schedule = (): void => {
    if (!this.#shown || !this.#mv) return;
    cancelAnimationFrame(this.#frame);
    // Hotspot-i dobiju nove pozicije tek u sledećem iscrtavanju model-viewer-a.
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update(): void {
    const mv = this.#mv;
    if (!mv) return;
    const theta = mv.getCameraOrbit().theta;
    const side = `${Math.cos(theta) >= 0 ? 'f' : 'b'}${Math.sin(theta) >= 0 ? 'r' : 'l'}`;
    if (side !== this.#side) {
      this.#side = side;
      const pts = cornerPoints(mv.getBoundingBoxCenter(), mv.getDimensions(), theta);
      for (const [name, p] of Object.entries(pts)) {
        mv.updateHotspot({ name: `hotspot-${name}`, position: `${p.x}m ${p.y}m ${p.z}m` });
      }
      // Novi položaji važe od sledećeg frejma.
      this.#frame = requestAnimationFrame(() => this.#update());
      return;
    }
    const w = this.#layer.clientWidth;
    const h = this.#layer.clientHeight;
    const screen = POINTS.map((n) => mv.queryHotspot(`hotspot-${n}`)?.canvasPosition);
    // Kota i spaja teme i sa temenom i + 1 (A–B, B–C, C–D).
    this.#parts.forEach(({ line, label }, i) => {
      const a = screen[i];
      const b = screen[i + 1];
      if (!a || !b) return;
      line.setAttribute('x1', String(a.x));
      line.setAttribute('y1', String(a.y));
      line.setAttribute('x2', String(b.x));
      line.setAttribute('y2', String(b.y));
      // Natpis ostaje u okviru i kad linija na uskom ekranu ide uz samu ivicu.
      const hw = label.offsetWidth / 2 + 4;
      const hh = label.offsetHeight / 2 + 4;
      label.style.left = `${clamp((a.x + b.x) / 2, hw, w - hw)}px`;
      label.style.top = `${clamp((a.y + b.y) / 2, hh, h - hh)}px`;
    });
  }
}
